import React, { useContext, useEffect, useMemo } from 'react';
import { useCategoryListQuery, useCurriculumDetailsQuery } from '../../../gql/hooks';
import { Translate } from 'app/services';
import { PageContainer, PageContent } from 'app/components/common';
import { LoadingPopup } from '../../common/learningListController/loadingPopup/LoadingPopup';
import { getDefaultCurriculumBanner } from '../../../const';
import LearningList from '../../common/learningList';
import './styles.less';
import Modal from 'antd/lib/modal/Modal';
import { InfoBanner } from '../../common/InfoBanner';
import ResourceList from '../../common/resourceList';
import { BreadcrumbContext, useBreadcrumbs } from '../../../breadcrumbs/BreadcrumbContext';
import { PaperIcon } from '@nackle/paper';
import { ACTIVE_THEME_NAME } from '../../../branding';
import { getString } from '@nackle/intl-tools';
import { Link } from '../../common';
import { CurriculumUtils } from '../../../utils';
import { Routes } from '../../../navigation';

export const CurriculumDetailsPage = ( props ) => {
    const curriculumId = useMemo( () => props?.route?.params?.curriculumId, [ props ] );

    const { data, loading: queryLoading, error, fetchAnew } = useCurriculumDetailsQuery( curriculumId );
    const { data: categoryData } = useCategoryListQuery();

    const { leaveCrumb } = useContext( BreadcrumbContext );
    const breadcrumbs = useBreadcrumbs();

    useEffect( () => {
        if ( props?.route?.params?.curriculumId ) {
            fetchAnew( props?.route?.params?.curriculumId );
        }
    }, [ props?.route?.params?.curriculumId ] );

    useEffect( () => {
        if ( data?.name ) {
            leaveCrumb( props.pageKey, data.name );
        }
    }, [ data?.name ] );

    const courseList = useMemo( () => {
        if ( !data || !data.courses ) {return [];}

        if ( data.prerequisiteData?.locked ) {
            return data.courses.map( c => {
                const coursePrereq = c.prerequisiteData ?? {};
                coursePrereq.locked = true;
                return {
                    ...c,
                    prerequisiteData: coursePrereq,
                    blockClick: true
                };
            } );
        }

        return data.courses;
    }, [ data ] );

    const bannerImageUrl = useMemo( () => {
        if ( data ) {
            const url = CurriculumUtils.getHeroImageUrl( data );
            return url ? url : getDefaultCurriculumBanner( ACTIVE_THEME_NAME );
        } else {
            return undefined;
        }
    }, [ data ] );

    const sidebarItems = useMemo( () => {
        if ( !data?.categories?.length > 0 || !categoryData?.length > 0 ) {
            return [];
        }

        const allValidCatIds = categoryData.map( c => c.id );
        const validCats = data.categories.filter( d => allValidCatIds.includes( d.categoryId ) );
        
        return [
            ( validCats.length > 0 ) ?
                <div className='category-parents' key='category-parents'>
                    <Translate.Text 
                            value="page.curriculumDetails.sections.categories.label" />
                        { validCats.map( c => <Link key={ c.categoryId } push to={ Routes.CategoryDetails.buildPath( c.categoryId ) }>{ c.name }</Link> ) }
                </div> : null
        ].filter( x => !!x ).flatMap( ( x, i ) => i == 0 ? [ x ] : [ <div className='divider' key={ `div-${ i }` } />, x ] );}
        , [ data, categoryData ] );

    return (
        <PageContent className="page-curriculum-details">
            <LoadingPopup visible={ queryLoading } />
            
            { /* Banner */ }
            <PageContainer className="curriculum-details-banner-container">

            <InfoBanner
                title={ data?.name }
                percentComplete={ data?.progress } 
                hidePercentage={ data?.prerequisiteData?.locked }
                isLoading= { queryLoading } 
                locked={ data?.prerequisiteData?.locked }
                imageSrc={ bannerImageUrl }
                type='curriculum'
            /> 

            { !queryLoading && data &&
            <>
                { data.prerequisiteData?.locked && 
                    <div className='locked-content'>
                        <span>
                            <PaperIcon collection='ultimate' icon='Lock4' />
                            { data.prerequisiteData.operator == 'AND' 
                                ? <Translate.Text value='learningObject.locked.curriculum.all'/> 
                                : <Translate.Text value='learningObject.locked.curriculum.any'/> 
                            }                        
                            { data.prerequisiteData.prerequisites
                                .filter( r => !r.completed )
                                .flatMap( ( req, i ) => ( [ ( i != 0 ? ',\u00A0' : '\u00A0' ), <Link key={ req.id } push to={ req.link }>{ req.name }</Link> ] ) ) }

                        </span>
                    </div>  
                }
                <div className='curriculum-contents-section'>

                    { breadcrumbs }

                    <div className='details'>
                        <h4>
                            <Translate.Text className='title' value="application.windowTitle.curriculumDetails" />
                        </h4>
                        <div className='_body'>
                            <span dangerouslySetInnerHTML={ { __html: data?.description } } />
                        </div>
                    </div>

                    <div className='course-list'>
                        <h4>
                            <Translate.Text className='title' value="page.curriculumDetails.sections.courses.header" />
                        </h4>
                        <LearningList
                            displayType='list'
                            learningType='course'
                            data={ courseList }
                            isLoading= { queryLoading } 
                        /> 
                    </div>

                    <ResourceList 
                        resourceIds={ data.lxp?.resourceIds }
                    />

                    { sidebarItems.length > 0 && 
                        <div className='sidebar'>
                            { sidebarItems }
                        </div>
                    }
                </div>
            </>
            }
                
            </PageContainer>
            <Modal
                className={ `${ ACTIVE_THEME_NAME } error-modal` }
                title={ error?.titleKey ? <h3>{ getString( error.titleKey ) }</h3> : '' }
                visible={ !!error }
                onOk={ () => {} }
                onCancel={ () => {} }
                closable={ false }
                footer={ <></> }
                >

                { error?.descriptionKey ? getString( error.descriptionKey ) : '' }
                
            </Modal>
       </PageContent>
    );
};